<template>
  <ImageElement
    v-if="renderWithoutFigure && !hasDarkSource"
    :id="id"
    ref="element"
    :src="src"
    :aspect-ratio="aspectRatio"
    :class="imageClass"
    :style="imageStyle"
    :alt="alt || ''"
    :height="height"
    :width="width"
    :lazy="lazy"
    :sizes="sizes"
    :do-not-convert="doNotConvert || isSvg"
    :preload="preload"
    :ai-watermark="aiWatermark"
  />
  <PictureElement
    v-else
    ref="element"
    :src="src"
    :src-sm="srcSm"
    :src-md="srcMd"
    :src-lg="srcLg"
    :src-xl="srcXl"
    :src-dark="srcDark"
    :src-sm-dark="srcSmDark"
    :src-md-dark="srcMdDark"
    :src-lg-dark="srcLgDark"
    :src-xl-dark="srcXlDark"
    :aspect-ratio="aspectRatio"
    :aspect-ratio-sm="aspectRatioSm"
    :aspect-ratio-md="aspectRatioMd"
    :aspect-ratio-lg="aspectRatioLg"
    :aspect-ratio-xl="aspectRatioXl"
    :image-style="imageStyle"
    :image-class="imageStyle"
    :alt="alt || ''"
    :height="height"
    :width="width"
    :lazy="lazy"
    :sizes="sizes"
    :do-not-convert="doNotConvert || isSvg"
    :preload="preload"
    :ai-watermark="aiWatermark"
  >
    <slot />
  </PictureElement>
</template>

<script setup lang="ts">
/**
 * When we habe 1 source for all viewports, we can use img with option srcset for high resolutions.
 * When we have different images for a breakpoint, we should use the "picture" element.
 */
import {
  PictureElement,
  ImageElement,
  type AiBadgeProps,
} from '@i22team/td-longtail-vue-components';

export type CdnImageProps = {
  id?: string | undefined;
  src?: string | undefined;
  srcSm?: string | undefined;
  srcMd?: string;
  srcLg?: string;
  srcXl?: string | undefined;

  // Alternative sources for `prefers-color-scheme: dark`. Renders a <picture>, even with renderWithoutFigure.
  srcDark?: string | undefined;
  srcSmDark?: string | undefined;
  srcMdDark?: string | undefined;
  srcLgDark?: string | undefined;
  srcXlDark?: string | undefined;
  height?: number | undefined;
  width?: number | undefined;
  renderWithoutFigure?: boolean;

  // Custom AspectRatio for each Breakpoint
  aspectRatio?: string;
  aspectRatioSm?: string;
  aspectRatioMd?: string;
  aspectRatioLg?: string;
  aspectRatioXl?: string;

  // From ImageElementProps
  imageClass?: string;
  imageStyle?: string;
  alt?: string;
  lazy?: boolean;
  format?: 'jpeg' | 'webp' | string;
  doNotConvert?: boolean;
  sizes?: string; // "sm:100vw md:50vw lg:400px" https://v0.image.nuxtjs.org/components/nuxt-img#sizes
  preload?: boolean;
  aiWatermark?: AiBadgeProps;
};

const props = withDefaults(defineProps<CdnImageProps>(), {
  id: undefined,
  src: undefined,
  srcSm: undefined,
  srcMd: undefined,
  srcLg: undefined,
  srcXl: undefined,
  srcDark: undefined,
  srcSmDark: undefined,
  srcMdDark: undefined,
  srcLgDark: undefined,
  srcXlDark: undefined,
  aspectRatio: undefined,
  aspectRatioSm: undefined,
  aspectRatioMd: undefined,
  aspectRatioLg: undefined,
  aspectRatioXl: undefined,
  width: undefined,
  height: undefined,
  imageStyle: undefined,
  imageClass: undefined,
  alt: '',
  format: 'webp',
  sizes: '',
  aiWatermark: undefined,
});

// We do not want to convert a svg.
const hasDarkSource = computed(() =>
  Boolean(props.srcDark || props.srcSmDark || props.srcMdDark || props.srcLgDark || props.srcXlDark)
);

const isSvg = computed(() => props.src?.endsWith('.svg'));
</script>
